'use client'
import { motion } from 'framer-motion'
import { useLang } from '../context/LangContext'
import { t, terms as termsT } from '../lib/translations'

const sectionKeys = ['acceptance', 'service', 'account', 'acceptableUse', 'billing', 'sla', 'dataOwnership', 'liability', 'termination', 'governingLaw', 'changes'] as const

const sectionContent: Record<string, { en: string; fa: string; ar: string }> = {
  acceptance: {
    en: 'By accessing or using any Lexoya cloud infrastructure services ("Services"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to all of these Terms, you may not access or use our Services. These Terms apply to all visitors, users, and customers of Lexoya, including individual users and entities acting on behalf of organizations.',
    fa: 'با دسترسی یا استفاده از هر یک از خدمات زیرساخت ابری Lexoya ("خدمات")، موافقت خود را با این قوانین و شرایط اعلام می‌کنید. اگر با تمام این شرایط موافق نیستید، نمی‌توانید به خدمات ما دسترسی داشته باشید.',
    ar: 'باستخدام أي من خدمات البنية التحتية السحابية لـ Lexoya ("الخدمات")، فإنك توافق على الالتزام بهذه الشروط والأحكام ("الشروط"). إذا كنت لا توافق على جميع هذه الشروط، فلا يجوز لك الوصول إلى خدماتنا.',
  },
  service: {
    en: 'Lexoya provides cloud infrastructure services including but not limited to virtual private servers (VPS), GPU-accelerated computing, platform-as-a-service (PaaS), managed databases, object storage, backup solutions, DevOps tooling, infrastructure monitoring, and technical consulting.',
    fa: 'Lexoya خدمات زیرساخت ابری شامل اما نه محدود به سرورهای خصوصی مجازی (VPS)، محاسبات شتاب‌یافته با GPU، پلتفرم به عنوان سرویس (PaaS)، پایگاه‌های داده مدیریت‌شده، ذخیره‌سازی اشیاء و راهکارهای پشتیبان‌گیری ارائه می‌دهد.',
    ar: 'توفر Lexoya خدمات البنية التحتية السحابية بما في ذلك على سبيل المثال لا الحصر الخوادم الخاصة الافتراضية (VPS) والحوسبة المتسارعة بـ GPU والمنصة كخدمة (PaaS).',
  },
  account: {
    en: 'You are responsible for maintaining the confidentiality of your account credentials, including your password and any API keys. You must notify us immediately of any unauthorized use of your account.',
    fa: 'شما مسئول حفظ محرمانگی اطلاعات حساب خود از جمله رمز عبور و کلیدهای API هستید. باید بلافاصله ما را از هرگونه استفاده غیرمجاز از حساب خود مطلع کنید.',
    ar: 'أنت مسؤول عن الحفاظ على سرية بيانات اعتماد حسابك، بما في ذلك كلمة المرور وأي مفاتيح API. يجب عليك إخطارنا فورًا بأي استخدام غير مصرح به لحسابك.',
  },
  acceptableUse: {
    en: 'You agree to use our Services only for lawful purposes and in compliance with all applicable laws. You may not use our Services to distribute malware, conduct denial-of-service attacks, host illegal content, engage in phishing or fraud, or infringe upon intellectual property rights.',
    fa: 'شما موافقت می‌کنید که از خدمات ما فقط برای اهداف قانونی و مطابق با قوانین قابل اجرا استفاده کنید. نمی‌توانید از خدمات ما برای توزیع بدافزار، حملات انکار سرویس یا میزبانی محتوای غیرقانونی استفاده کنید.',
    ar: 'توافق على استخدام خدماتنا فقط للأغراض القانونية ووفقًا لجميع القوانين المعمول بها. لا يجوز لك استخدام خدماتنا لتوزيع البرامج الضارة أو شن هجمات حجب الخدمة.',
  },
  billing: {
    en: 'Services are billed on a monthly or hourly basis depending on the plan. All fees are non-refundable except as expressly stated in our refund policy. Accounts with past-due balances may be suspended after a grace period.',
    fa: 'خدمات بر اساس طرح به صورت ماهانه یا ساعتی صورتحساب می‌شوند. همه هزینه‌ها غیرقابل استرداد هستند مگر در مواردی که در سیاست بازپرداخت ما ذکر شده است.',
    ar: 'تتم فوترة الخدمات على أساس شهري أو كل ساعة حسب الخطة. جميع الرسوم غير قابلة للاسترداد إلا على النحو المنصوص عليه في سياسة الاسترداد الخاصة بنا.',
  },
  sla: {
    en: 'Lexoya guarantees a 99.99% network uptime for all paid services, measured monthly. If we fail to meet this commitment, you may be eligible for a service credit. The SLA excludes scheduled maintenance and force majeure events.',
    fa: 'Lexoya ۹۹.۹۹٪ زمان کار شبکه را برای همه خدمات پولی تضمین می‌کند. اگر نتوانیم به این تعهد عمل کنیم، ممکن است واجد شرایط دریافت اعتبار خدماتی شوید.',
    ar: 'تضمن Lexoya تشغيل الشبكة بنسبة ٩٩.٩٩٪ لجميع الخدمات المدفوعة، تُقاس شهريًا. إذا فشلنا في الوفاء بهذا الالتزام، فقد تكون مؤهلاً للحصول على رصيد خدمة.',
  },
  dataOwnership: {
    en: 'You retain full ownership of all data, content, and applications you store or process using our Services. We do not claim any intellectual property rights over your data.',
    fa: 'شما مالکیت کامل همه داده‌ها، محتوا و برنامه‌هایی را که با استفاده از خدمات ما ذخیره یا پردازش می‌کنید حفظ می‌کنید. ما هیچ حق مالکیت معنوی بر داده‌های شما ادعا نمی‌کنیم.',
    ar: 'تحتفظ بالملكية الكاملة لجميع البيانات والمحتوى والتطبيقات التي تخزنها أو تعالجها باستخدام خدماتنا. لا ندعي أي حقوق ملكية فكرية على بياناتك.',
  },
  liability: {
    en: 'To the maximum extent permitted by law, Lexoya shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or in connection with these Terms or the use of our Services.',
    fa: 'تا حداکثر میزان مجاز توسط قانون، Lexoya در قبال هیچ خسارت غیرمستقیم، اتفاقی، ویژه، تبعی یا تنبیهی ناشی از این شرایط یا استفاده از خدمات ما مسئول نخواهد بود.',
    ar: 'إلى أقصى حد يسمح به القانون، لن تكون Lexoya مسؤولة عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية تنشأ عن أو تتعلق بهذه الشروط.',
  },
  termination: {
    en: 'Either party may terminate these Terms at any time with written notice. Upon termination, your right to access and use the Services ceases immediately. You are responsible for exporting your data before termination.',
    fa: 'هر یک از طرفین می‌توانند این شرایط را در هر زمان با اعلام کتبی فسخ کنند. پس از فسخ، حق دسترسی و استفاده از خدمات بلافاصله پایان می‌یابد.',
    ar: 'يجوز لأي من الطرفين إنهاء هذه الشروط في أي وقت بإخطار كتابي. عند الإنهاء، ينتهي حقك في الوصول إلى الخدمات واستخدامها فورًا.',
  },
  governingLaw: {
    en: 'These Terms shall be governed by and construed in accordance with the laws of Iran, without regard to its conflict of law provisions. Any disputes shall be resolved exclusively in the courts of Tehran, Iran.',
    fa: 'این شرایط توسط قوانین ایران اداره و تفسیر می‌شود. هرگونه اختلاف منحصراً در دادگاه‌های تهران، ایران حل و فصل خواهد شد.',
    ar: 'تخضع هذه الشروط وتُفسر وفقًا لقوانين إيران. يتم حل أي نزاعات حصريًا في محاكم طهران، إيران.',
  },
  changes: {
    en: 'We reserve the right to modify these Terms at any time. Material changes will be communicated via email at least thirty (30) days before they take effect.',
    fa: 'ما حق تغییر این شرایط را در هر زمان برای خود محفوظ می‌داریم. تغییرات مهم حداقل سی (۳۰) روز قبل از اجرایی شدن از طریق ایمیل اعلام خواهد شد.',
    ar: 'نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سيتم الإبلاغ عن التغييرات الجوهرية عبر البريد الإلكتروني قبل ثلاثين (٣٠) يومًا على الأقل من تاريخ سريانها.',
  },
}

const lastUpdated = 'January 1, 2025'

export default function TermsPage() {
  const lang = useLang()

  return (
    <>
      <section className="relative pt-32 pb-24 sm:pt-40 sm:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#4f46e5]/5 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-[#6366f1]/15 rounded-full blur-[160px] pointer-events-none opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-[#6366f1]/15 rounded-full blur-[140px] pointer-events-none opacity-75" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[300px] bg-[#6366f1]/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 inset-x-0 h-40 bg-gradient-to-t from-[#07070d] via-[#07070d]/80 to-transparent pointer-events-none z-10" />

        <div className="relative max-w-3xl mx-auto px-8 sm:px-8 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-sm font-medium text-[#818cf8] tracking-wide uppercase">{t(lang, termsT.subtitle)}</span>
            <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-white">
              {t(lang, termsT.heading)}
            </h1>
            <p className="mt-4 text-sm text-[#64748b]">{t(lang, termsT.lastUpdated)} {lastUpdated}</p>
            <p className="mt-6 text-lg text-[#94a3b8] leading-relaxed">
              {t(lang, termsT.intro)}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="relative py-24 sm:py-32 bg-white/[0.005]">
        <div className="absolute inset-0 dot-pattern pointer-events-none" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#4f46e5]/[0.02] to-transparent pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-8 sm:px-8 lg:px-8">
          <div className="space-y-16">
            {sectionKeys.map((key, i) => {
              const content = sectionContent[key]
              const contentText = lang === 'fa' ? content.fa : lang === 'ar' ? content.ar : content.en
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                >
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                    {t(lang, termsT.sections[key].title)}
                  </h2>
                  <p className="text-[#94a3b8] leading-relaxed">{contentText}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>
    </>
  )
}
